import React, { useState, useEffect, useCallback } from 'react';
import { WizardShell } from './WizardShell';
import { useWizardNavigation } from '../../wizard-framework';
import { Step1_RequirementInfo } from './steps/Step1_RequirementInfo';
import { Step2_InformationCapture } from './steps/Step2_InformationCapture';
import { Step3_Acknowledgement } from './steps/Step3_Acknowledgement';
import { Step4_PdfGeneration } from './steps/Step4_PdfGeneration';
import { Step5_CompletionSummary } from './steps/Step5_CompletionSummary';

/**
 * DocumentCompletionWizard
 *
 * Root component for multi-step document completion flow
 *
 * Architecture:
 * - Uses useWizardNavigation for abstract navigation state
 * - Maintains domain-specific wizardData state separately
 * - Steps communicate validation via setCanAdvance callback
 *
 * 5-Step Process:
 * 1. Select template and establish context (club, team, participant)
 * 2. Capture required form field data with validation
 * 3. Acknowledge/accept document before generation
 * 4. Generate PDF and update document status
 * 5. Download and confirm completion
 */
const DocumentCompletionWizard = ({
  document,
  template,
  templates = [],
  clubs = [],
  teams = [],
  participants = [],
  club_id,
  onClose,
  onSuccess
}) => {
  const TOTAL_STEPS = 5;

  // Abstract wizard navigation state (separated from domain state)
  const {
    currentStep,
    goToNext,
    goToPrevious,
    goToStep,
    setCanAdvance,
    setIsLoading,
    buttonStates
  } = useWizardNavigation(TOTAL_STEPS, {
    initialStep: 1,
    onStepChange: (step) => {
      console.log('[Wizard] Navigated to step:', step);
    }
  });

  // Domain-specific wizard data state - persists across all steps
  const [wizardData, setWizardData] = useState({
    // Step 1: Template & Context
    templateId: document?.template_id || '',
    templateName: '',
    club_id: club_id || '',
    clubName: '',
    teamId: '',
    teamName: '',
    participantId: '',
    participantName: '',
    trackingReferenceId: '', // Optional user-entered tracking reference (e.g., request ID)

    // Step 2: Form data
    formData: {},

    // Step 3: Acknowledgement
    acknowledged: false,

    // Step 4-5: Completion status
    documentId: '', // Assignment ID - required for completion submission
    pdfUrl: '',
    completed_at: null
  });

  // Initialize with provided document/template data
  useEffect(() => {
    if (document) {
      setWizardData(prev => ({
        ...prev,
        documentId: document.id || document.assignment_id || prev.documentId,
        templateId: document.template_id || prev.templateId,
        club_id: document.club_id || club_id || prev.club_id,
        teamId: document.team_id || prev.teamId,
        participantId: document.participant_id || prev.participantId
      }));
    }
  }, [document, club_id]);

  // Get current template details
  const currentTemplate = wizardData.templateId
    ? templates.find(t => t.id === wizardData.templateId) || template
    : template;

  // Handle step data changes (domain state)
  const handleDataChange = useCallback((newData) => {
    setWizardData(newData);
  }, []);

  // Handle step validation (drives wizard navigation state)
  const handleValidationChange = useCallback((isValid) => {
    setCanAdvance(isValid);
  }, [setCanAdvance]);

  // Finish wizard
  const handleFinish = useCallback(() => {
    if (onSuccess) {
      onSuccess({
        documentId: wizardData.documentId,
        pdfUrl: wizardData.pdfUrl,
        completed_at: wizardData.completed_at
      });
    }
    onClose();
  }, [wizardData, onSuccess, onClose]);

  // Handle PDF generation completion
  const handlePdfSuccess = useCallback((response) => {
    console.log('[Wizard] PDF generated successfully:', response);
    // Auto-advance to completion summary
    goToStep(TOTAL_STEPS);
    setIsLoading(false);
  }, [goToStep, setIsLoading]);

  const handlePdfError = useCallback((error) => {
    console.error('[Wizard] PDF generation error:', error);
    setIsLoading(false);
  }, [setIsLoading]);

  // Render current step component
  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1_RequirementInfo
            wizardData={wizardData}
            templates={templates}
            clubs={clubs}
            teams={teams}
            participants={participants}
            onDataChange={handleDataChange}
            onValidationChange={handleValidationChange}
          />
        );

      case 2:
        return (
          <Step2_InformationCapture
            wizardData={wizardData}
            template={currentTemplate}
            onDataChange={handleDataChange}
            onValidationChange={handleValidationChange}
          />
        );

      case 3:
        return (
          <Step3_Acknowledgement
            wizardData={wizardData}
            template={currentTemplate}
            onDataChange={handleDataChange}
            onValidationChange={handleValidationChange}
          />
        );

      case 4:
        return (
          <Step4_PdfGeneration
            wizardData={wizardData}
            template={currentTemplate}
            onSuccess={handlePdfSuccess}
            onError={handlePdfError}
            onDataChange={handleDataChange}
          />
        );

      case 5:
        return (
          <Step5_CompletionSummary
            wizardData={wizardData}
            template={currentTemplate}
            onFinish={handleFinish}
          />
        );

      default:
        return null;
    }
  };

  return (
    <WizardShell
      title="Complete Document"
      totalSteps={TOTAL_STEPS}
      currentStep={currentStep}
      onNext={goToNext}
      onPrevious={goToPrevious}
      onFinish={handleFinish}
      onClose={onClose}
      buttonStates={buttonStates}
      nextButtonLabel={currentStep === 1 ? 'Continue' : 'Next'}
      previousButtonLabel="Back"
      finishButtonLabel={currentStep === TOTAL_STEPS ? 'Done' : 'Finish'}
      isLoading={buttonStates.isLoading}
    >
      {renderCurrentStep()}
    </WizardShell>
  );
};

export { DocumentCompletionWizard };
